import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { App } from './App';
import { AuthProvider } from './auth/AuthContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import { applyStoredTheme } from './hooks/useTheme';
import './styles/globals.css';

// Apply the persisted theme override (or fall through to the OS preference)
// before the first React paint, so there's no light-then-dark flash.
applyStoredTheme();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Tasks are cheap and idempotent — refetch on focus by default so
      // returning to a tab shows fresh state.
      refetchOnWindowFocus: true,
      // Don't retry on auth failures — the API client throws
      // NotAuthenticatedError, which the AuthContext listener handles by
      // redirecting to /login.
      retry: (failureCount, error) => {
        const name = (error as Error)?.name;
        if (name === 'NotAuthenticatedError') return false;
        // AccountBlockedError is terminal — retrying just re-hits the same 403.
        if (name === 'AccountBlockedError') return false;
        return failureCount < 1;
      },
      staleTime: 15_000,
    },
  },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <App />
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
);
